<template>
  <BlogPostLayout>
    <template #image>
      <BlogHeader
        :title="postData.title"
        :excerpt="postData.excerpt"
        :image="postData.image"
        :author="postData.author"
        :date="postData.date"
        :updatedAt="postData.updatedAt"
        :categories="postData.categories"
        :readTime="postData.readTime"
      />
    </template>

    <template #content>
      <article class="blog-content">
        <section class="intro">
          <p>
            Shooting excellent 360 photos with your phone requires understanding
            a small number of critical techniques that make the difference
            between a seamless, professional result and a blurry, poorly
            stitched image. The phone's camera hardware is not the limiting
            factor — technique and preparation determine 95% of the final
            quality. This guide covers everything you need to capture
            publication-ready 360 photos using any modern smartphone. Get started
            with
            <a :href="`${mainSiteUrl}/360camera`">Travvir's AI-powered 360 Camera app</a>.
          </p>
        </section>

        <section>
          <h2>Equipment Setup Before You Shoot</h2>

          <h3>Tripod: Non-Negotiable for Good Results</h3>
          <p>
            Hand-holding a phone during 360 capture introduces camera shake that
            creates misalignment between frames and visible stitching errors. A
            stable tripod is the single most impactful piece of equipment you
            can add to your 360 photography setup. You need a tripod that can
            position the phone at approximately
            <strong>1.5–1.7 metres height</strong> (standing eye level) and
            rotate smoothly around the vertical axis.
          </p>
          <p>
            A basic £15–25 tripod with a phone mount is sufficient for indoor
            photography. For outdoor shooting in wind or uneven terrain, a
            heavier tripod with a ball head provides more stability. Ensure the
            tripod head is level before shooting — most apps detect level
            positioning, but manual levelling improves stitching accuracy.
          </p>

          <h3>Phone Settings</h3>
          <p>
            Before starting your capture session, configure your phone camera
            settings:
            <strong>Lock exposure manually</strong> to prevent the auto-exposure
            from changing as you rotate (exposure shifts between frames cause
            visible bands in the stitched result). On iPhone, tap and hold to
            lock AE/AF before capture. On Android, use the manual or Pro mode to
            set a fixed ISO and shutter speed.
            <strong>Turn off HDR mode</strong> — automatic HDR takes multiple
            captures per frame and causes motion ghosting in 360 stitching.
            <strong>Enable grid lines</strong>
            to help maintain consistent phone levelling throughout the rotation.
          </p>

          <h3>App Configuration</h3>
          <p>
            Open Travvir and navigate to the 360° capture mode. The app displays
            a spherical overlay showing the required capture zones. Review the
            settings: set output resolution to maximum for best quality, ensure
            auto-stitch is enabled, and if available, set stitching quality to
            "high" (this increases processing time but improves seam quality).
          </p>
        </section>

        <section>
          <h2>The Capture Technique</h2>

          <h3>Rotation Speed and Method</h3>
          <p>
            The single most common mistake in phone 360 photography is rotating
            too quickly. Each frame needs sufficient overlap with adjacent
            frames for the stitching algorithm to find matching features between
            them. A rotation rate of approximately
            <strong>one frame every 2–3 seconds</strong>, moving about 15–20
            degrees per step, gives reliable results. The app provides visual
            guidance on each required capture zone — wait for each zone's
            indicator to confirm capture before moving to the next.
          </p>

          <h3>The Pivot Point Rule</h3>
          <p>
            When rotating, always rotate around the phone's camera lens — not
            around your own body or the tripod head. This minimises
            <strong>parallax error</strong>: the apparent shift in the position
            of foreground objects relative to background objects as the
            viewpoint moves. Parallax error is the primary cause of stitching
            anomalies in 360 photos — particularly visible as "ghosting" of
            objects near the camera.
          </p>
          <p>
            Most 360-capable tripod heads have an adjustment to position the
            lens directly over the rotation axis. If your tripod head does not,
            position the phone as close to the rotation centre as possible, with
            the lens at the forward edge of the mount.
          </p>

          <h3>Capturing the Nadir (Floor) and Zenith (Ceiling)</h3>
          <p>
            Many 360 apps automatically fill in the nadir (directly below the
            camera) with a logo or blur. If you want a complete floor view, most
            apps include a nadir capture mode where you tilt the phone down and
            capture the floor area. Similarly, the zenith (ceiling/sky directly
            above) can be captured for a complete spherical image. For most
            business virtual tour applications, the default nadir fill is
            acceptable.
          </p>
        </section>

        <section>
          <h2>Lighting for 360 Photography</h2>

          <h3>The Biggest Challenge</h3>
          <p>
            Lighting is more critical in 360 photography than in standard
            photography because the image captures the entire environment
            including light sources, windows, and artificial lighting in all
            directions simultaneously. The camera must balance bright windows
            against darker interior areas across the full sphere — a challenge
            that no auto-exposure system handles perfectly.
          </p>

          <h3>Indoor Lighting Approach</h3>
          <p>
            For indoor spaces: Turn on
            <strong>all interior lights</strong> before shooting. Close curtains
            or blinds on windows with direct sunlight to reduce contrast.
            Position your camera away from direct windows — shooting with a
            window behind you produces better exposure balance than shooting
            toward bright windows. Avoid mixed light temperatures (warm
            incandescent + cool fluorescent) as this creates colour
            inconsistencies across the stitched sphere.
          </p>

          <h3>Outdoor Shooting</h3>
          <p>
            For outdoor 360 photos, overcast sky conditions provide the most
            even lighting. Direct sun creates harsh shadows that rotate around
            the scene with each frame, creating visible inconsistencies in the
            stitched result. If shooting in direct sun is unavoidable,
            <strong>golden hour</strong> (60 minutes after sunrise or before
            sunset) provides warm, low-contrast light that stitches more cleanly
            than midday sun.
          </p>
        </section>

        <section>
          <h2>Shooting Positions and Tour Flow</h2>

          <h3>Planning Your Shooting Positions</h3>
          <p>
            For a virtual tour of a space, plan your shooting positions before
            starting. Each position should provide a complete, coherent view of
            an area and be approximately 2–4 metres apart. Mark positions on a
            rough floor plan sketch — this ensures you cover the full space and
            prevents redundant captures.
          </p>
          <p>
            For a standard room: one central position captures the full space.
            For large open areas (gym floor, warehouse, open-plan office):
            capture from 3–5 positions to allow navigation across the full area.
            For long narrow spaces (corridors, hallways): one position every 5–6
            metres provides good coverage.
          </p>

          <h3>Creating Natural Tour Flow</h3>
          <p>
            Position your shooting spots to create a natural walking path
            through the space. When viewers navigate your tour, each transition
            should feel intuitive — moving from entrance to main area to
            secondary areas follows the natural exploration path visitors would
            take in person. Avoid shooting in dead-end arrangements where
            navigation requires "teleporting" back to the entrance.
          </p>
        </section>

        <section>
          <h2>Post-Capture Review and Common Fixes</h2>

          <h3>What to Check After Stitching</h3>
          <p>
            After the app processes your captures, review the 360 image for:
            visible stitch lines (indicating insufficient frame overlap or rapid
            rotation), exposure banding (horizontal bands of different
            brightness — caused by auto-exposure changing between frames),
            ghosting of moving objects, and missing coverage areas (black
            patches in the image indicating uncaptured zones).
          </p>

          <h3>When to Reshoot</h3>
          <p>
            Reshoot a position if you see: obvious stitch lines across the
            middle of the image, blurred areas from camera movement during
            capture, or more than 10% of the sphere showing black patches. The
            most common reason for needing a reshoot is rotation speed — slowing
            down your rotation is the first fix to attempt. For the specific
            technical causes of blurry results and how to fix them, see our
            guide on
            <a href="/blog/en/fix-blurry-360-photos">fixing blurry 360 photos</a>.
          </p>
        </section>

        <section>
          <h2>Conclusion: Consistent Technique Creates Professional Results</h2>
          <p>
            The difference between amateur and professional-looking 360 photos
            from a smartphone is almost entirely down to consistent technique:
            stable tripod positioning, controlled rotation speed, correct camera
            settings (locked exposure, no HDR), and appropriate lighting
            preparation. The phone's camera hardware is rarely the limiting
            factor.
          </p>
          <p>
            Master these fundamentals and your first session will produce
            results that are genuinely competitive with dedicated 360 cameras
            costing hundreds of pounds. More importantly, they'll produce
            virtual tours that convert viewers into customers and stand out from
            the low-quality content that most businesses currently deploy.
          </p>
        </section>
        <section>
          <h2>Continue Reading</h2>
          <ul>
          <li><a :href="`${mainSiteUrl}/360camera`">Travvir 360 Camera App</a></li>
          <li><a :href="`${mainSiteUrl}/panorama`">Travvir 360 Panorama App</a></li>
          <li><a href="/blog/en/how-to-take-360-photos-smartphone">360 Photo Basics and Camera Settings</a></li>
          <li><a href="/blog/en/what-is-a-360-photo">What Is a 360 Photo?</a></li>
          <li><a href="/blog/en/interior-photography-360-virtual-tour">Interior 360 Photography Tips</a></li>
          <li><a href="/blog/en/360-virtual-tour-image-size">Best Image Size for 360 Tours</a></li>
          <li><a href="/blog/en/fix-blurry-360-photos">Fix Blurry 360 Photos</a></li>
          <li><a href="/blog/en/360-photography-mistakes-to-avoid">Common 360 Photography Mistakes</a></li>
          <li><a href="/blog/en/360-virtual-tour-smartphone-guide">Create a Virtual Tour with Your Phone</a></li>
          </ul>
        </section>

      </article>
    </template>
  </BlogPostLayout>
</template>

<script setup>
import blogPostsData from "~/data/blogPosts.json";

const config = useRuntimeConfig();
const cdnUrl = config.public.cdnUrl;
const mainSiteUrl = config.public.baseUrl;

const postData = blogPostsData.find(
  (post) => post.slug === "shoot-360-photos-with-phone",
);

useBlogSeo({
  title: "Shoot Professional 360 Photos with Your Phone | Travvir",
  description:
    "Master professional 360 photo technique using only your phone. Covers lighting, positioning, stitching, and editing for stunning virtual tour images.",
  keywords:
    "shoot 360 photos phone, 360 photography tips, how to take 360 photos smartphone, 360 photo technique, phone 360 photography guide",
  slug: "shoot-360-photos-with-phone",
  image: postData?.image,
  postData,
  lang: "en",
});

</script>
